import { type ReactNode } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@monksflow/monks-ui';

export interface SegmentedControlOption<Value extends string> {
  value: Value;
  label: ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps<Value extends string> {
  value: Value;
  onValueChange: (value: Value) => void;
  options: ReadonlyArray<SegmentedControlOption<Value>>;
  className?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

/**
 * The mock's light-track, raised-white-pill segmented control. Tessra's `Tabs` already
 * produces exactly this treatment out of the box — `TabsList` is the light track
 * (`bg-surface`) and the active `TabsTrigger` renders an animated `bg-background` pill —
 * so this only adapts that component to a plain `options` array. No Tessra styling is
 * overridden here; only compose its documented props.
 */
export function SegmentedControl<Value extends string>({
  value,
  onValueChange,
  options,
  className,
  ...rest
}: SegmentedControlProps<Value>) {
  return (
    <Tabs
      value={value}
      onValueChange={(next) => onValueChange(next as Value)}
      className={className}
    >
      <TabsList {...rest}>
        {options.map((option) => (
          <TabsTrigger key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}
